import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin } from "lucide-react";
import { avgScore, slugify } from "../mock";
import { usePlaces } from "../context/PlacesContext";
import { PlaceCard, SectionLabel } from "../components/Bits";

export default function Neighborhood() {
  const { slug } = useParams();
  const { places, loading } = usePlaces();

  if (loading) {
    return <div className="pt-[140px] pb-40 text-center text-ink-soft font-serif text-2xl">Loading…</div>;
  }

  const matched = places.filter((p) => slugify(p.neighborhood) === slug);
  const first = matched[0];

  if (!first) {
    return (
      <div className="pt-[140px] pb-40 text-center">
        <h1 className="font-serif text-4xl">Neighbourhood not found.</h1>
        <Link to="/explore" className="inline-block mt-6 text-claret link-underline">Back to Explore</Link>
      </div>
    );
  }

  const name = first.neighborhood;
  const cover = first.image;
  const avg = Math.round((matched.reduce((s, p) => s + avgScore(p.scores), 0) / matched.length) * 10) / 10;
  const mapQuery = `${name}, ${first.city}, ${first.country}`;

  return (
    <div className="pt-[68px]">
      <section className="relative h-[52vh] min-h-[380px] flex items-end overflow-hidden">
        <div className="absolute inset-0 kenburns">
          <img src={cover} alt={name} className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 w-full pb-12">
          <Link to={`/city/${slugify(first.city)}`} className="inline-flex items-center gap-2 text-paper/70 hover:text-paper text-sm mb-6">
            <ArrowLeft className="w-4 h-4" /> {first.city}
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8 bg-brass" />
            <span className="eyebrow text-brass">Neighbourhood · {first.city}</span>
          </div>
          <h1 className="font-serif text-paper text-5xl sm:text-6xl md:text-8xl font-semibold leading-none">{name}</h1>
          <div className="flex items-center gap-6 mt-6 text-paper/80 text-sm">
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {first.city}, {first.country}</span>
            <span>{matched.length} reviewed {matched.length === 1 ? "place" : "places"}</span>
            <span>{matched.filter((p) => (p.distinction || 0) > 0).length} with distinction</span>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <SectionLabel>The neighbourhood in brief</SectionLabel>
        <p className="font-serif text-2xl sm:text-3xl md:text-4xl leading-snug mt-6 max-w-3xl display-balance">
          {name} is one of {first.city}'s most rewarding corners to eat and drink in — and our reviewers
          keep finding reasons to return.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-12">
          <div>
            <h2 className="font-serif text-3xl font-semibold mb-8">Featured places</h2>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-14">
              {matched.map((p, i) => <PlaceCard key={p.id} place={p} index={i} />)}
            </div>
          </div>
          <div className="lg:sticky lg:top-24 self-start">
            <div className="border border-ink/12 overflow-hidden bg-paper">
              <iframe
                title={`${name} map`}
                src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=14&output=embed`}
                className="w-full h-[520px] grayscale-[0.15]"
                loading="lazy"
                style={{ border: 0 }}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
